import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, authAxios } from './adminApi';
import { useAdminAuth } from './useAdminAuth';
import { useProgramActions } from './useProgramActions';

export const useAdminData = () => {
  const auth = useAdminAuth();
  const { isAuthenticated, dropSession } = auth;

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [programStats, setProgramStats] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentAssessments, setRecentAssessments] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, assessmentsRes, programsRes, programStatsRes, flagsRes, statsRes] = await Promise.all([
        authAxios.get(`${API}/admin/users`),
        authAxios.get(`${API}/admin/assessments`),
        axios.get(`${API}/programs`),
        authAxios.get(`${API}/admin/program-stats`),
        authAxios.get(`${API}/admin/feature-flags`),
        authAxios.get(`${API}/admin/stats`)
      ]);

      setRecentUsers(usersRes.data.slice(0, 10));
      setRecentAssessments(assessmentsRes.data.slice(0, 10));

      setStats({
        totalUsers: statsRes.data.total_users,
        totalAssessments: statsRes.data.total_assessments,
        totalRevenue: statsRes.data.total_revenue,
        paidReports: statsRes.data.paid_assessments
      });

      setPrograms(programsRes.data.programs || []);
      setProgramStats(programStatsRes.data);
      setFeatureFlags(flagsRes.data.flags || []);
    } catch (err) {
      if (err.response?.status === 401) {
        dropSession();
      }
    } finally {
      setLoading(false);
    }
  }, [dropSession]);

  useEffect(() => { if (isAuthenticated) fetchAll(); }, [isAuthenticated, fetchAll]);

  const programActions = useProgramActions(fetchAll);

  const toggleFlag = async (flagName, currentValue) => {
    try {
      await authAxios.post(`${API}/admin/feature-flag/${flagName}?enabled=${!currentValue}`);
      setFeatureFlags(prev => prev.map(f => f.flag_name === flagName ? { ...f, enabled: !currentValue } : f));
    } catch {
      alert('Failed to toggle flag');
    }
  };

  return {
    isAuthenticated,
    checkingAuth: auth.checkingAuth,
    loginError: auth.loginError,
    loginLoading: auth.loginLoading,
    handleLogin: auth.handleLogin,
    handleLogout: auth.handleLogout,
    loading, stats, programStats, programs, featureFlags, recentUsers, recentAssessments,
    ...programActions,
    toggleFlag,
  };
};
